import {type Moment} from 'moment-timezone';
import i18next from 'i18next';

import {hasValue} from '@famly/stat_ts-utils_has-value';
import {AutomaticInvoicingOccurrence, BillingSchemeName, FinancePeriod} from 'web-app/util/graphql/api-types';
import {isEmpty} from 'web-app/util/array';
import type {ValidateBillingPlanMutationResult} from 'web-app/api/clients/rest-types/validate-billing-plan/validate-billing-plan';
import type {
    PreviewPlansQuery,
    PreviewPlansQueryVariables,
} from 'web-app/react/plans/manage-plan/__generated__/queries.api-types';
import * as Warnable from 'web-app/util/warnable';
import * as Failable from 'web-app/util/failable';
import {head, isNonEmpty} from '@famly/stat_ts-utils_non-empty-array';

import type * as ApiTypes from '../api-types';
import type * as Types from '../types';
import {
    AUTOMATIC_PRICING_GROUP_ID,
    STATIC_PRODUCT_CREATION_PRICING_PROPS,
    STATIC_PRODUCT_CREATION_PROPS,
    STATIC_SESSION_PACKAGE_CREATION_PRICING_PROPS,
    STATIC_SESSION_PACKAGE_CREATION_PROPS,
} from '../constants';
import type {PricingGroupsForChild} from '../hooks/use-pricing-group-for-adhoc-purchase';
import {getDay} from '../helpers/helpers';

const transformApiSessionToTuition =
    ({
        billingProfiles,
        hiddenTuitionsToDisplay = [],
    }: {
        billingProfiles: Types.BillingProfile[];
        hiddenTuitionsToDisplay?: Array<Types.Tuition['id']>;
    }) =>
    (
        session: ApiTypes.ApiSession | ApiTypes.ApiSessionPackageCreationResult,
    ): Warnable.Warnable<null | Types.Tuition, string> => {
        const apiPricings = session.pricing;
        const pricingGroups = transformApiPricingGroupsToPricingGroups(apiPricings);
        const shouldDisplay = !session.hiddenByUser || hiddenTuitionsToDisplay.includes(session.id);
        if (!isNonEmpty(apiPricings) || !isNonEmpty(pricingGroups)) {
            return Warnable.warningMessage(null, 'transformApiSessionToTuition: Session is not valid');
        }
        if (!shouldDisplay) {
            // We do not want warn about sessions that are not visible
            return Warnable.resolve(null);
        }
        switch (head(apiPricings).period) {
            case 'MONTHLY': {
                const billingProfile = billingProfiles.find(billingProfile => billingProfile.type === 'monthly');
                return hasValue(billingProfile)
                    ? Warnable.resolve({
                          type: 'monthly',
                          billingProfile,
                          id: session.id,
                          title: session.title,
                          pricingGroups,
                          availability: session.availability,
                      })
                    : Warnable.warningMessage(null, 'transformApiSessionToTuition: Monthly billing profile not found');
            }
            case 'WEEKLY': {
                const billingProfile = billingProfiles.find(billingProfile => billingProfile.type === 'weekly');
                return hasValue(billingProfile)
                    ? Warnable.resolve({
                          type: 'weekly',
                          billingProfile,
                          id: session.id,
                          title: session.title,
                          pricingGroups,
                          availability: session.availability,
                      })
                    : Warnable.warningMessage(null, 'transformApiSessionToTuition: Weekly billing profile not found');
            }
            default:
                return Warnable.warningMessage(null, 'transformApiSessionToTuition: Unrecognized billing period');
        }
    };

const transformApiPricingGroupsToPricingGroups = (
    apiPricingGroups: Array<ApiTypes.ApiPricingGroup>,
): Array<Types.PricingGroup> => {
    return apiPricingGroups
        .map(apiPricingGroup => {
            if (!hasValue(apiPricingGroup.pricingGroupId)) {
                return null;
            }
            return {
                id: apiPricingGroup.pricingGroupId,
                price: apiPricingGroup.price,
            };
        })
        .filter(hasValue);
};

const transformApiBillingProfileToBillingProfile = (
    billingProfile: ApiTypes.ApiBillingProfile,
): Warnable.Warnable<Types.BillingProfile | null, string> => {
    if (!hasValue(billingProfile.attendanceSchedule)) {
        return Warnable.warningMessage(
            null,
            'transformApiBillingProfileToBillingProfile: Api billing profile does not contain an attendance schedule',
        );
    }

    if (!billingProfile.attendanceSchedule.fullYear) {
        // A business decision was made to get rid of billing profiles that do not have a full year schedule
        // This is due to the fact that we only want to handle billing profiles that we automatically create for customers (which have a full year schedule)
        // In this case we do not want to send a warning, as it is expected that some billing profiles do not have a full year schedule
        return Warnable.resolve(null);
    }

    switch (billingProfile.billingScheme.id) {
        case 'MONTHLY': {
            return Warnable.resolve({
                type: 'monthly',
                id: billingProfile.billingProfileId,
                title: billingProfile.title,
                billingScheme: billingProfile.billingScheme,
                attendanceScheduleId: billingProfile.attendanceSchedule.id,
            });
        }
        case 'ACTUALS_V2': {
            return Warnable.resolve({
                type: 'weekly',
                id: billingProfile.billingProfileId,
                title: billingProfile.title,
                billingScheme: billingProfile.billingScheme,
                attendanceScheduleId: billingProfile.attendanceSchedule.id,
            });
        }
        default:
            return Warnable.warningMessage(
                null,
                `transformApiBillingProfileToBillingProfile: Unrecognized billing scheme id "${billingProfile.billingScheme.id}"`,
            );
    }
};

const transformTuitionsToApiSessionBookings = (tuitions: Types.Tuition[]): ApiTypes.ApiSessionBooking[] => {
    return tuitions.flatMap(tuition => {
        return tuition.availability.map(availability => {
            return {
                sessionId: tuition.id,
                fundable: false,
                day: availability.day,
                start: availability.start,
                end: availability.end,
            };
        });
    });
};

const transformTuitionsToApiSessionBookingsForEditView = (
    selectedTuitions: Types.Tuition[],
    initialPlanTuitions: Types.Plan['tuitions'],
): ApiTypes.ApiSessionBooking[] => {
    const mergedTuitions = mergeSelectedTuitionsAvailabilityWithAvailabilityFromInitialPlanTuitions(
        selectedTuitions,
        initialPlanTuitions,
    );

    return mergedTuitions.flatMap(tuition => {
        return tuition.availability.map(availability => {
            return {
                sessionId: tuition.id,
                fundable: false,
                day: availability.day,
                start: availability.start,
                end: availability.end,
            };
        });
    });
};

/* When editing a plan, we need to preserve the child attendance from
 * existing billing plan. This merges the selected tuition with the availability
 * stored in existing plan */
const mergeSelectedTuitionsAvailabilityWithAvailabilityFromInitialPlanTuitions = (
    selectedTuitions: Types.Tuition[],
    initialPlanTuitions: Types.Plan['tuitions'],
): Types.Tuition[] => {
    return selectedTuitions.map(selectedTuition => {
        const matchingTuitionFromInitialPlan = initialPlanTuitions.find(
            initialPlanTuition => initialPlanTuition.id === selectedTuition.id,
        );

        if (matchingTuitionFromInitialPlan) {
            return {
                ...selectedTuition,
                availability: matchingTuitionFromInitialPlan.availability,
            };
        }
        return selectedTuition;
    });
};

const transformApiInvoicingPeriodToInvoicingPeriod = (
    config: ApiTypes.ApiAutomaticInvoicingConfig,
): Types.AutoBillingPeriod | null => {
    const invoicingPeriod = config?.invoicingPeriod;
    if (!invoicingPeriod) {
        return null;
    }

    switch (invoicingPeriod) {
        case 'NEXT': {
            return 'NEXT';
        }
        case 'CURRENT': {
            return 'CURRENT';
        }
        case 'PREVIOUS': {
            return 'PREVIOUS';
        }
        default: {
            return null;
        }
    }
};

const transformApiAutomaticInvoicingConfigToBillingFrequencies = (
    billingFrequencies: ApiTypes.ApiAutomaticInvoicingConfig,
): Types.BillingFrequency[] => {
    const monthlyBillingFrequency: Types.MonthlyBillingFrequency | null = billingFrequencies?.monthly
        ? {
              type: 'monthly',
              autoBillingPeriod: transformApiInvoicingPeriodToInvoicingPeriod(billingFrequencies),
          }
        : null;
    const weeklyBillingFrequency: Types.WeeklyBillingFrequency | null = billingFrequencies?.weekly
        ? {
              type: 'weekly',
              autoBillingPeriod: transformApiInvoicingPeriodToInvoicingPeriod(billingFrequencies),
          }
        : null;
    const biweeklyBillingFrequency: Types.BiweeklyBillingFrequency | null = billingFrequencies?.biweekly
        ? {
              type: 'biweekly',
              startDate: billingFrequencies?.biweekly.startDate,
              autoBillingPeriod: transformApiInvoicingPeriodToInvoicingPeriod(billingFrequencies),
          }
        : null;
    return [monthlyBillingFrequency, weeklyBillingFrequency, biweeklyBillingFrequency].filter(hasValue);
};

const transformApiProductToFee =
    ({hiddenFeesToDisplay}: {hiddenFeesToDisplay: Array<Types.Fee['id']>}) =>
    (product: ApiTypes.ApiProduct | ApiTypes.ApiCreatedProduct): null | Types.Fee => {
        const pricings = product.pricing;
        const pricingGroups = transformApiPricingGroupsToPricingGroups(pricings);
        const shouldDisplay = !product.hiddenByUser || hiddenFeesToDisplay.includes(product.id);

        if (!isNonEmpty(pricings) || !isNonEmpty(pricingGroups) || !shouldDisplay) {
            return null;
        }

        switch (head(pricings).period) {
            case 'WEEKLY': {
                return {
                    type: 'weekly',
                    id: product.id,
                    title: product.title,
                    pricingGroups,
                    isDiscountable: product.discountable,
                };
            }
            case 'MONTHLY': {
                return {
                    type: 'monthly',
                    id: product.id,
                    title: product.title,
                    pricingGroups,
                    isDiscountable: product.discountable,
                };
            }
            default:
                return null;
        }
    };

const transformFeesToApiProductBookings = (
    allFees: Array<Types.Fee | undefined>,
): Array<ApiTypes.ApiProductBooking> => {
    // We must count the amount for each tuition to create product bookings
    // We take the fees and count how many times they appear in the array,
    // while we construct a new object where key is the feeId and value is the amount

    const fees = allFees.filter(hasValue);

    const amounts = fees.reduce<Record<Types.Fee['id'], number>>(
        (accumulator, current) => ({
            ...accumulator,
            [current.id]: (accumulator[current.id] || 0) + 1,
        }),
        {},
    );

    const uniqueFees = [...new Set(fees)];

    return uniqueFees
        .map(fee => {
            const amount = amounts[fee.id];

            if (!hasValue(amount)) {
                return null;
            }

            return {
                productId: fee.id,
                amount,
                day: 'MONDAY',
            };
        })
        .filter(hasValue);
};

const transformBillingFrequencyToApiSchedule = (
    billingFrequencyOption: Types.BillingFrequencyOption,
): null | ApiTypes.ApiSchedule => {
    switch (billingFrequencyOption?.type) {
        case 'monthly':
            return AutomaticInvoicingOccurrence.MONTHLY;
        case 'weekly':
            return AutomaticInvoicingOccurrence.WEEKLY;
        case 'biweekly':
            return AutomaticInvoicingOccurrence.BIWEEKLY;
        case 'none':
        default:
            return null;
    }
};

const transformApiBillPayerIdsToBillPayers = (
    apiBillPayerIds: Array<ApiTypes.ApiBillPayer>,
): Array<Types.BillPayer> => {
    return apiBillPayerIds.map(billPayerId => ({
        id: billPayerId,
    }));
};

const transformProratedItemsToApiProductCreations = ({
    proratedItems,
    siteId,
    pricingGroups,
    selectedPricingGroupId,
    suffix,
}: {
    proratedItems: Types.ProratedItemWithBillingType[];
    siteId: string;
    pricingGroups: Array<Types.PricingGroupOption>;
    selectedPricingGroupId: Types.State['pricingGroupState']['selectedPricingGroup'];
    suffix: string;
}): ApiTypes.ApiProductCreation[] => {
    return proratedItems.map(proratedItem => ({
        institutionId: siteId,
        title: `${proratedItem.title} ${suffix}`,
        pricing:
            selectedPricingGroupId === AUTOMATIC_PRICING_GROUP_ID
                ? pricingGroups.map(pricingGroup => ({
                      pricingGroupId: pricingGroup.value,
                      price: proratedItem.price,
                      adhocPrice: proratedItem.price,
                      pricing: proratedItem.type === 'monthly' ? 'MONTHLY' : 'WEEKLY',
                      period: proratedItem.type === 'monthly' ? 'MONTHLY' : 'WEEKLY',
                      ...STATIC_PRODUCT_CREATION_PRICING_PROPS,
                  }))
                : [
                      {
                          pricingGroupId: selectedPricingGroupId,
                          price: proratedItem.price,
                          adhocPrice: proratedItem.price,
                          pricing: proratedItem.type === 'monthly' ? 'MONTHLY' : 'WEEKLY',
                          period: proratedItem.type === 'monthly' ? 'MONTHLY' : 'WEEKLY',
                          ...STATIC_PRODUCT_CREATION_PRICING_PROPS,
                      },
                  ],
        ...STATIC_PRODUCT_CREATION_PROPS,
    }));
};

const transformProratedTuitionsToApiSessionPackageCreations = (
    tuitions: Types.Tuition[],
    institutionId: string,
    pricingGroups: Array<Types.PricingGroupOption>,
    selectedPricingGroupId: Types.State['pricingGroupState']['selectedPricingGroup'],
    periodType: 'start' | 'end',
): ApiTypes.ApiSessionPackageCreation[] => {
    /**
     * Most of these hardcoded values are here only for the sake of the testing.
     * They will be adjusted according to feedback from US support team.
     */
    return tuitions
        .map<ApiTypes.ApiSessionPackageCreation | null>(tuition => {
            if (periodType === 'start') {
                return tuition.customStartPeriod
                    ? {
                          institutionId,
                          title: `${tuition.customStartPeriod.title} ${i18next.t(
                              'react.simplifiedUsBillingPlan.translations.json.customPeriod.chargedAsPurchase',
                          )}`,
                          pricing:
                              selectedPricingGroupId === AUTOMATIC_PRICING_GROUP_ID
                                  ? pricingGroups.map(pricingGroup => ({
                                        pricingGroupId: pricingGroup.value,
                                        pricing: tuition.type === 'monthly' ? 'MONTHLY' : 'WEEKLY',
                                        period: tuition.type === 'monthly' ? 'MONTHLY' : 'WEEKLY',
                                        ...STATIC_SESSION_PACKAGE_CREATION_PRICING_PROPS,
                                    }))
                                  : [
                                        {
                                            pricingGroupId: selectedPricingGroupId,
                                            pricing: tuition.type === 'monthly' ? 'MONTHLY' : 'WEEKLY',
                                            period: tuition.type === 'monthly' ? 'MONTHLY' : 'WEEKLY',
                                            ...STATIC_SESSION_PACKAGE_CREATION_PRICING_PROPS,
                                        },
                                    ],
                          availability: tuition.availability.map(availability => {
                              return {
                                  sessionId: tuition.id,
                                  fundable: false,
                                  day: getDay(availability.day),
                                  start: availability.start,
                                  end: availability.end,
                              };
                          }),
                          ...STATIC_SESSION_PACKAGE_CREATION_PROPS,
                      }
                    : null;
            }

            return tuition.customEndPeriod
                ? {
                      institutionId,
                      title: `${tuition.customEndPeriod.title} (charged as a purchase)`,
                      pricing:
                          selectedPricingGroupId === AUTOMATIC_PRICING_GROUP_ID
                              ? pricingGroups.map(pricingGroup => ({
                                    pricingGroupId: pricingGroup.value,
                                    pricing: tuition.type === 'monthly' ? 'MONTHLY' : 'WEEKLY',
                                    period: tuition.type === 'monthly' ? 'MONTHLY' : 'WEEKLY',
                                    ...STATIC_SESSION_PACKAGE_CREATION_PRICING_PROPS,
                                }))
                              : [
                                    {
                                        pricingGroupId: selectedPricingGroupId,
                                        pricing: tuition.type === 'monthly' ? 'MONTHLY' : 'WEEKLY',
                                        period: tuition.type === 'monthly' ? 'MONTHLY' : 'WEEKLY',
                                        ...STATIC_SESSION_PACKAGE_CREATION_PRICING_PROPS,
                                    },
                                ],
                      availability: tuition.availability.map(availability => {
                          return {
                              sessionId: tuition.id,
                              fundable: false,
                              day: getDay(availability.day),
                              start: availability.start,
                              end: availability.end,
                          };
                      }),
                      ...STATIC_SESSION_PACKAGE_CREATION_PROPS,
                  }
                : null;
        })
        .filter(hasValue);
};

const transformTuitionsWithOverriddenPriceToApiSessionPackageCreation = (
    tuitions: NonEmptyArray<Types.Tuition>,
    institutionId: string,
): {newTuition: ApiTypes.ApiSessionPackageCreation; oldTuitionId: Types.Tuition['id']}[] => {
    return tuitions
        .map(tuition => {
            const newPrice = tuition.overriddenPrice;
            if (!hasValue(newPrice)) {
                return null;
            }

            return {
                newTuition: {
                    institutionId,
                    title: tuition.title,
                    pricing: tuition.pricingGroups.map<ApiTypes.ApiSessionPackageCreation['pricing'][number]>(
                        pricingGroup => ({
                            pricingGroupId: pricingGroup.id,
                            ...STATIC_SESSION_PACKAGE_CREATION_PRICING_PROPS,
                            price: newPrice,
                            volume: 1,
                            pricing: tuition.billingProfile.type === 'monthly' ? 'MONTHLY' : 'WEEKLY',
                            period: tuition.billingProfile.type === 'monthly' ? 'MONTHLY' : 'WEEKLY',
                        }),
                    ),
                    availability: tuition.availability.map(availability => {
                        return {
                            sessionId: tuition.id,
                            fundable: false,
                            day: getDay(availability.day),
                            start: availability.start,
                            end: availability.end,
                        };
                    }),
                    ...STATIC_SESSION_PACKAGE_CREATION_PROPS,
                },
                oldTuitionId: tuition.id,
            };
        })
        .filter(hasValue);
};

type TransformChildrenAndFeeToApiAdhocPurchases = SetUpTransformChildrenAndFeeToApiAdhocPurchases;
type SetUpTransformChildrenAndFeeToApiAdhocPurchases = (
    pricingGroupsForChildren: Array<PricingGroupsForChild>,
    purchaseDate: Moment,
) => ExecuteTransformChildrenAndFeeToApiAdhocPurchases;
type ExecuteTransformChildrenAndFeeToApiAdhocPurchases = (
    fees: ApiTypes.ApiCreatedProduct[],
) => ApiTypes.ApiAdhocPurchase[];
const transformChildrenAndFeeToApiAdhocPurchases: TransformChildrenAndFeeToApiAdhocPurchases =
    (pricingGroupsForChildren, purchaseDate) => fees => {
        const purchaseDayOfWeek = purchaseDate.format('dddd').toUpperCase();

        return pricingGroupsForChildren.map(pricingForChild => ({
            childId: pricingForChild.childId,
            discountAmount: '0',
            productBookings: fees
                .map(fee => {
                    const feesAvailabilityId = fee.availabilityIdsByDays.find(
                        availability => availability.day === purchaseDayOfWeek,
                    );
                    if (!feesAvailabilityId) {
                        return null;
                    }
                    return {
                        amount: 1,
                        bookedPrice: fee.pricing
                            .find(pricing => pricing.pricingGroupId === pricingForChild.pricingGroupId)
                            ?.adhocPrice?.toString(),
                        pricingGroupId: pricingForChild.pricingGroupId,
                        productAvailabilityId: feesAvailabilityId.productAvailabilityId,
                        purchaseDate: purchaseDate.format('YYYY-MM-DD'),
                    };
                })
                .filter(hasValue),
            sessionBookings: [],
        }));
    };

const transformApiDiscountToDiscount = (discount: ApiTypes.ApiDiscount): Types.Discount => {
    return {
        id: discount.discountPresetId,
        title: discount.title,
        amount: discount.amount,
        ordering: discount.ordering,
        type: 'preset',
    };
};

const transformDiscountsToApiDiscounts = (discounts: Array<Types.Discount>): Array<ApiTypes.ApiDiscountInput> => {
    if (isEmpty(discounts)) {
        return [];
    }

    return discounts.map((discount, index) => {
        if (discount.type === 'preset') {
            return {
                amount: discount.amount,
                discountPresetId: discount.id,
                isPercent: true,
                ordering: index,
                origin: 'preset',
                period: 'WEEKLY',
                showOnInvoice: true,
                title: discount.title,
            };
        }

        if (discount.customDiscountType === 'percentage') {
            return {
                amount: discount.amount,
                isPercent: true,
                ordering: index,
                origin: 'custom',
                period: 'WEEKLY',
                showOnInvoice: true,
                title: discount.title,
            };
        }

        return {
            amount: discount.amount,
            isPercent: false,
            ordering: index,
            origin: 'custom',
            period: discount.period === 'monthly' ? 'MONTHLY' : 'WEEKLY',
            showOnInvoice: true,
            title: discount.title,
        };
    });
};
/**
 * Automatic pricing group should be submitted as null when creating plans
 * This is due to how the API works
 */
const transformPlanPricingGroupIdToApiPlanPricingGroupId = (
    pricingGroupId: null | Types.PricingGroup['id'],
): null | string => (pricingGroupId === AUTOMATIC_PRICING_GROUP_ID ? null : pricingGroupId);

const transformApiValidatedPlanToPlanValidationResult = (
    validatedPlan: ValidateBillingPlanMutationResult,
): Types.PlanValidationResult => {
    const warnings =
        validatedPlan.validateBillingPlan.plan.behaviors
            .filter(behavior => behavior.id === 'ShowPlanWarnings')
            .flatMap(behavior => behavior.payload?.warnings ?? [])
            .filter(hasValue) ?? [];

    const childId = validatedPlan.validateBillingPlan.plan.childId;

    if (warnings.length > 0) {
        return {
            type: 'error',
            errors: warnings.map(warning => ({
                message: warning.message,
                error: warning.error,
                severity: warning.severity,
                title: warning.title,
                metadata: {
                    childId,
                    changeFrom: warning.metadata?.changeFrom ?? '',
                    invoiceIds: warning.metadata?.invoiceIds ?? [''],
                    startDate: warning.metadata?.startDate ?? '',
                    endDate: warning.metadata?.endDate ?? '',
                },
            })),
        };
    }

    return {type: 'success'};
};

const transformApiPreviewPlanToPlanValidationResult = (
    previewPlan: PreviewPlansQuery['finance']['plans']['preview'][number],
): Types.PlanValidationResult => {
    const planWarningBehavior = previewPlan.behaviors.find(behavior => behavior.id === 'ShowPlanWarnings');
    if (!hasValue(planWarningBehavior)) {
        return {type: 'success'};
    }

    // This payload parsing assumes a specific response structure, particularly when accessing `warnings`.
    // This approach is potentially risky, as it may cause runtime errors if the structure changes or is not as expected.
    // Ideally, we should use a validation/parsing library like Zod to ensure type safety and guard against malformed responses.
    const {warnings}: {warnings: Types.PlanValidationError[]} = JSON.parse(
        planWarningBehavior.payload ?? '{warnings: []}',
    );
    const errors = warnings.map(warning => ({
        message: warning.message,
        error: warning.error,
        severity: warning.severity,
        title: warning.title,
        metadata: warning.metadata,
    }));
    return {
        type: 'error',
        errors,
    };
};

const transformSubmissionDataPlansToPlanInput = (
    plans: Array<ApiTypes.ApiPlanInput>,
    automaticRuleGroupId: string | null,
    oldPlanId: string,
    isPlanPartiallyBilled: boolean,
): Failable.Failable<PreviewPlansQueryVariables['planInputs'], string[]> => {
    const planInputs: Failable.Failable<PreviewPlansQueryVariables['planInputs'][number], string>[] = plans.map(
        plan => {
            if (!hasValue(automaticRuleGroupId) && !hasValue(plan.input.plan.pricingGroupId)) {
                return Failable.fail('Automatic rule group and pricing group are both null');
            }

            const pricing = plan.input.plan.pricingGroupId
                ? {
                      pricingGroupId: plan.input.plan.pricingGroupId,
                      ruleGroupId: null,
                  }
                : {
                      pricingGroupId: null,
                      ruleGroupId: automaticRuleGroupId,
                  };

            return Failable.resolve({
                oldPlanId: isPlanPartiallyBilled ? undefined : oldPlanId,
                childId: plan.input.plan.childId,
                note: plan.input.plan.note,
                planParts: plan.input.plan.planParts.map(part => transformPlanPartToPlanPartInput(part)),
                pricing,
                range: {
                    from: plan.input.plan.from,
                    to: plan.input.plan.to,
                },
            });
        },
    );

    return Failable.all(planInputs);
};

const transformPlanPartToPlanPartInput = (
    part: ApiTypes.ApiPlanInput['input']['plan']['planParts'][number],
): PreviewPlansQueryVariables['planInputs'][number]['planParts'][number] => {
    return {
        ordering: part.ordering,
        billingProfileId: part.billingProfileId,
        sessionBookings: part.sessionBookings.map(sessionBooking => ({
            allowFunding: sessionBooking.fundable,
            dayOfWeek: sessionBooking.day,
            sessionId: sessionBooking.sessionId,
            range: {
                from: sessionBooking.start,
                to: sessionBooking.end,
            },
        })),
        productBookings: part.productBookings.map(productBooking => ({
            amount: productBooking.amount,
            dayOfWeek: productBooking.day,
            productId: productBooking.productId,
            bookedPrice: productBooking.bookedPrice?.toString(),
            planRuleId: productBooking.planRuleId,
        })),
        termTimeOnly: part.termTimeOnly,
        attendanceScheduleId: part.attendanceScheduleId,
        billingScheme: part.billing.id === 'MONTHLY' ? BillingSchemeName.MONTHLY : BillingSchemeName.ACTUALS_V2,
        invoices: part.billing.invoices,
        priceModifiers: part.discounts.map(transformApiDiscountToPriceModifier),
        weeksOfCare: part.billing.weeksOfCare,
    };
};

export const transformApiDiscountToPriceModifier = (
    discount: ApiTypes.ApiPlanInput['input']['plan']['planParts'][number]['discounts'][number],
): PreviewPlansQueryVariables['planInputs'][number]['planParts'][number]['priceModifiers'][number] => {
    if (discount.origin === 'preset') {
        return {
            presetDiscount: {
                discountPresetId: discount.discountPresetId,
                ordering: discount.ordering,
                period: discount.period === 'WEEKLY' ? FinancePeriod.WEEKLY : FinancePeriod.MONTHLY,
            },
        };
    }
    return {
        discount: {
            amount: discount.amount,
            percent: discount.isPercent,
            ordering: discount.ordering,
            period: discount.period === 'WEEKLY' ? FinancePeriod.WEEKLY : FinancePeriod.MONTHLY,
            showOnInvoice: discount.showOnInvoice,
            title: discount.title,
        },
    };
};

const transformApiPlanInputToApiValidatePlanInput = (plan: ApiTypes.ApiPlanInput): ApiTypes.ApiValidatePlanInput => {
    return {
        id: plan.input.plan.id,
        childId: plan.input.plan.childId,
        pricingGroupId: plan.input.plan.pricingGroupId,
        note: '',
        from: plan.input.plan.from,
        to: plan.input.plan.to,
        planParts: plan.input.plan.planParts,
    };
};

export {
    transformApiProductToFee,
    transformApiSessionToTuition,
    transformFeesToApiProductBookings,
    transformTuitionsToApiSessionBookings,
    transformApiBillingProfileToBillingProfile,
    transformApiAutomaticInvoicingConfigToBillingFrequencies,
    transformBillingFrequencyToApiSchedule,
    transformApiBillPayerIdsToBillPayers,
    transformProratedItemsToApiProductCreations,
    transformProratedTuitionsToApiSessionPackageCreations,
    transformChildrenAndFeeToApiAdhocPurchases,
    transformApiDiscountToDiscount,
    transformDiscountsToApiDiscounts,
    transformPlanPricingGroupIdToApiPlanPricingGroupId,
    transformTuitionsWithOverriddenPriceToApiSessionPackageCreation,
    transformApiValidatedPlanToPlanValidationResult,
    transformApiPreviewPlanToPlanValidationResult,
    transformSubmissionDataPlansToPlanInput,
    transformTuitionsToApiSessionBookingsForEditView,
    mergeSelectedTuitionsAvailabilityWithAvailabilityFromInitialPlanTuitions,
    transformApiPlanInputToApiValidatePlanInput,
};

export type {ExecuteTransformChildrenAndFeeToApiAdhocPurchases};
